import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
	ProSidebar,
	Menu,
	MenuItem,
	SidebarHeader,
	SidebarContent,
	SubMenu,
	SidebarFooter
} from "react-pro-sidebar";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp, faUserTag, faKey } from '@fortawesome/free-solid-svg-icons';
import { FiLogOut, FiUser } from "react-icons/fi";
import { FiArrowLeftCircle, FiArrowRightCircle } from "react-icons/fi";
import "react-pro-sidebar/dist/css/styles.css";
import './Sidebar.css';
import { setCookie, deleteCookie } from '../../store/localStorage';

import { logoutRequest } from '../../modules/login/services/LoginActions';
import { UPDATE_COMMON_REDUCER, SET_AUTHORIZED_USER } from '../../modules/common/services/CommonActionType';
import ResetPassword from '../../modules/login/view/model/ResetPassword';
import { SIDE_BAR_MENUS } from '../../constants/sidebarMenus';
import { BASE_PATH_PREFIX, DATA_CENTER_AUTOMATION } from '../../constants/routes';
import Loader from '../loader/view/Loader';
import { ROLES } from '../../constants/roleConstants';
import { FaRegFolderOpen } from 'react-icons/fa6';
import { getDocumentTypesWithConfigurationRequest } from '../../modules/common/services/CommonActions';
import { isMobileViewport } from '../../utils/viewport';

const Sidebar = (props) => {
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const sidebarContentRef = useRef(null);

	const { menuCollapse, menuIconClick, userData } = props;
	const [sidebarMenus, setSidebarMenus] = useState(SIDE_BAR_MENUS);
	const [showDropdown, setShowDropdown] = useState(false);
	const [openSubMenu, setOpenSubMenu] = useState(null);
	const [isRoleChanging, setIsRoleChanging] = useState(false);
	const [hoveredMenuLabel, setHoveredMenuLabel] = useState(null);
	const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
	const tooltipTimeoutRef = useRef(null);
	const [isMobile, setIsMobile] = useState(isMobileViewport());

	useEffect(() => {
		const handleResize = () => setIsMobile(isMobileViewport());
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);
	const { selectedRole, showResetPasswordModal, documentTypesWithConfiguration, selectedAuthorizedUser } = useSelector(state => state.common);
	// Set initial selected role from userData and dispatch to Redux
	// useEffect(() => {
	// 	//debugger;
	// 	if (userData?.rolesList?.items && userData.rolesList.items.length > 0 && !selectedRole) {
	// 		const defaultRole = userData.rolesList.items[0];
	// 		//setCookie('selectedRole', defaultRole);
	// 		dispatch({ type: UPDATE_COMMON_REDUCER, payload: { selectedRole: defaultRole } });
	// 	}
	// }, [userData?.rolesList?.items, selectedRole, dispatch]);

	// on mobile, sign-out lives outside the dropdown, so only show the caret when there's other content.
	// Must mirror every condition that can put something inside the dropdown below (Reset Password,
	// Authorized User selector, Role selector) — otherwise mobile users can have a dropdown item that's
	// impossible to reach, because the caret/click-to-open never appears for them to open it.
	const hasDropdownContent =
		Boolean(userData?.hasPassword) ||
		(userData?.rolesList?.items?.length > 1) ||
		(userData?.primaryUsers?.primaryUsers?.length > 0);

	const handleLogout = () => {
		deleteCookie('selectedRole');
		dispatch(logoutRequest());
		// since we use location, from state logic in Public/Private routes, need to reset the state here
		navigate(BASE_PATH_PREFIX, { replace: true });
	}

	const handleResetPassword = () => {
		dispatch({
			type: UPDATE_COMMON_REDUCER,
			payload: { showResetPasswordModal: true }
		});
		setShowDropdown(false);
	};

	const handleCloseResetPasswordModal = () => {
		dispatch({
			type: UPDATE_COMMON_REDUCER,
			payload: { showResetPasswordModal: false }
		});
	};

	const handleSubmitResetPassword = (data) => {
		// TODO: Implement API call for password reset
		handleCloseResetPasswordModal();
	};

	// Function to check if a menu item is active based on current pathname
	const isMenuActive = (menu) => {
		// Exact match for menu link
		if (menu.link && location.pathname === menu.link) {
			return true;
		}
		// // Special case for My Investment - check if viewing own investor details
		// if (menu.title === 'My Investment' && location.pathname.includes('/investment/investors/details/')) {
		// 	console.log('Checking My Investment active state');
		// 	const pathParts = location.pathname.split('/');
		// 	const userIdFromPath = pathParts[pathParts.length - 1];
		// 	if (userData?.userId && String(userIdFromPath) === String(userData.userId)) {
		// 		return true;
		// 	}
		// }
		// Check if current pathname starts with the menu link (for child routes)
		if (menu.link && location.pathname.startsWith(menu.link + '/')) {
			return true;
		}
		// Check if any submenu is active
		if (menu.subMenus && menu.subMenus.length > 0) {
			return menu.subMenus.some(subMenu => isMenuActive(subMenu));
		}
		return false;
	};

	// Function to check if a submenu item is specifically active (exact match)
	const isSubMenuItemActive = (menu) => {
		// Exact match
		if (menu.link && location.pathname === menu.link) {
			return true;
		}
		// // Special case for My Investment - check if viewing own investor details
		// if (menu.title === 'My Investment' && location.pathname.includes('/investment/investors/details/')) {
		// 	const pathParts = location.pathname.split('/');
		// 	const userIdFromPath = pathParts[pathParts.length - 1];
		// 	if (userData?.userId && String(userIdFromPath) === String(userData.userId)) {
		// 		return true;
		// 	}
		// }
		return false;
	};

	// Handle click on parent menu when sidebar is collapsed
	const handleParentMenuClick = (menuIndex) => {
		if (menuCollapse) {
			// Expand the sidebar first
			menuIconClick();
			// Set which submenu should be opened after a short delay to allow sidebar expansion
			setTimeout(() => {
				setOpenSubMenu(menuIndex);
				// Keep the submenu open until manually closed
			}, 200);
		}
	};

	// Handle tooltip positioning on menu hover
	const handleMenuHover = (e, label) => {
		if (!menuCollapse) {
			setHoveredMenuLabel(null);
			return;
		}
		// Clear any existing timeout
		if (tooltipTimeoutRef.current) {
			clearTimeout(tooltipTimeoutRef.current);
		}
		const rect = e.currentTarget.getBoundingClientRect();
		setTooltipPosition({
			top: rect.top + rect.height / 2,
			left: rect.right + 10
		});
		// Show tooltip after a small delay
		tooltipTimeoutRef.current = setTimeout(() => {
			setHoveredMenuLabel(label);
		}, 200);
	};

	const handleMenuLeave = () => {
		// Clear timeout if still waiting
		if (tooltipTimeoutRef.current) {
			clearTimeout(tooltipTimeoutRef.current);
		}
		// Immediately hide tooltip
		setHoveredMenuLabel(null);
	};

	// Helper function to find the scrollable container
	const findScrollableContainer = () => {
		if (!sidebarContentRef.current) return null;

		// Try multiple approaches to find the scrollable container

		// 1. Look for the pro-sidebar-content class specifically
		let scrollContainer = sidebarContentRef.current.querySelector('.pro-sidebar-content');
		if (scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight) {
			return scrollContainer;
		}

		// 2. Check if the ref itself is scrollable
		if (sidebarContentRef.current.scrollHeight > sidebarContentRef.current.clientHeight) {
			return sidebarContentRef.current;
		}

		// 3. Look for any scrollable element within the sidebar
		const allElements = sidebarContentRef.current.querySelectorAll('*');
		for (let element of allElements) {
			const style = getComputedStyle(element);
			if ((style.overflow === 'auto' || style.overflow === 'scroll' ||
				style.overflowY === 'auto' || style.overflowY === 'scroll') &&
				element.scrollHeight > element.clientHeight) {
				return element;
			}
		}

		// 4. Fallback to the sidebar content ref
		return sidebarContentRef.current;
	};

	// Helper function to scroll to active menu (can be called programmatically)
	const scrollToActiveMenu = () => {
		if (!sidebarContentRef.current || menuCollapse) return;

		setTimeout(() => {
			const activeMenuItems = sidebarContentRef.current?.querySelectorAll('.active');
			if (activeMenuItems && activeMenuItems.length > 0) {
				const activeMenuItem = activeMenuItems[activeMenuItems.length - 1];
				const scrollContainer = findScrollableContainer();

				if (scrollContainer && activeMenuItem) {
					// Simple approach: just scroll the element into view
					activeMenuItem.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
						inline: 'nearest'
					});
				}
			}
		}, 200);
	};

	// Reset openSubMenu when sidebar is collapsed
	useEffect(() => {
		if (menuCollapse) {
			setOpenSubMenu(null);
		} else {
			// When sidebar expands, scroll to active menu after a delay
			// setTimeout(() => {
			// }, 600); // Increased delay for sidebar expansion
			scrollToActiveMenu();
		}
	}, [menuCollapse]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (tooltipTimeoutRef.current) {
				clearTimeout(tooltipTimeoutRef.current);
			}
		};
	}, []);
	useEffect(() => {
		dispatch(getDocumentTypesWithConfigurationRequest());
	}, [dispatch]);

	// Update sidebar menus based on documentTypesWithConfiguration
	useEffect(() => {
		if (!documentTypesWithConfiguration?.length) return;

		const dataCenterSubMenus = documentTypesWithConfiguration.map(docType => ({
			title: docType.name,
			icon: <FaRegFolderOpen />,
			link: `${DATA_CENTER_AUTOMATION}/${docType.id}`,
			roles: [ROLES.ADMIN, ROLES.SUPER_ADMIN]
		}));

		const updatedMenus = SIDE_BAR_MENUS.map(menu => {
			if (menu.link === DATA_CENTER_AUTOMATION) {
				return {
					...menu,
					subMenus: dataCenterSubMenus
				}
			}
			return menu;
		});
		setSidebarMenus(updatedMenus);
	}, [documentTypesWithConfiguration]);

	// Close the sidebar automatically after picking a menu item — mobile only.
	// Desktop keeps its current collapsed/expanded state as-is.
	const handleMenuItemClick = () => {
		if (isMobile && !menuCollapse) {
			menuIconClick();
		}
	};

	// Recursive function to render menu items with unlimited nesting
	const renderMenuItems = (menuItems = []) => {
		const filteredMenuItems = menuItems.filter(menu => {
			// Show My Profile if:
			// - selectedAuthorizedUser is not set (normal login)
			// - OR selectedAuthorizedUser.userId === userData.userId
			if (menu.title === 'My Profile') {
				if (
					selectedAuthorizedUser && selectedAuthorizedUser.userId !== userData.userId
				) {
					return false;
				}
			}
			return menu?.roles?.includes(selectedRole?.code);
		});
		return filteredMenuItems.map((menu, index) => {
			const isActive = isMenuActive(menu);
			const isExactMatch = isSubMenuItemActive(menu);
			// shouldBeOpen should be true if:
			// 1. The menu or any of its submenus is active AND sidebar is not collapsed
			// 2. OR if this menu was manually opened via click when collapsed
			const shouldBeOpen = (isActive && !menuCollapse) || openSubMenu === index;

			// If the menu has subMenus, render as SubMenu with recursive children
			if (menu.subMenus && menu.subMenus.length > 0) {
				return (
					<SubMenu
						key={`submenu-${index}-${shouldBeOpen ? 'open' : 'closed'}`}
						title={menu.title}
						icon={menu.icon}
						className={`${(menuCollapse && isActive) ? 'active' : ''}`}
						defaultOpen={shouldBeOpen}
						onClick={() => handleParentMenuClick(index)}
						onMouseEnter={(e) => handleMenuHover(e, menu.title)}
						onMouseLeave={handleMenuLeave}
					>
						{renderMenuItems(menu.subMenus)}
					</SubMenu>
				);
			}
			// Otherwise, render as a regular MenuItem
			return (
				<MenuItem
					key={index}
					icon={menu.icon}
					className={isActive ? 'active' : ''}
					onMouseEnter={(e) => handleMenuHover(e, menu.title)}
					onMouseLeave={handleMenuLeave}
				>
					<Link to={menu.link} onClick={handleMenuItemClick}>{menu.title}</Link>
				</MenuItem>
			);
		});
	};

	return (
		<Loader show={isRoleChanging}>
			{/* Mobile overlay — closes sidebar when tapping outside */}
			{isMobile && !menuCollapse && (
				<div
					className="mobile-sidebar-overlay active"
					onClick={menuIconClick}
				/>
			)}
			<div className={`sidebar-container${isMobile ? (menuCollapse ? ' mobile-collapsed' : ' mobile-open') : ''}`}>
				<div className={`pro-sidebar ${menuCollapse ? "w-auto" : ""}`}>
					<div id="header">
						<ProSidebar collapsed={menuCollapse}>
							<SidebarHeader className='position-relative'>
								<div className={"logotext d-flex align-items-center justify-content-center h-100"}>
									{!menuCollapse && <img className="cursor-pointer d-inline" src="/assets/logos/RPLongLogo.png" alt="" style={{ width: 210 }} />}
									{menuCollapse && <img className="cursor-pointer" src="/assets/logos/RPShortLogo.jpg" alt="" style={{ width: 35, height: 35 }} />}
								</div>
								<div className="closemenu" onClick={menuIconClick}>
									{menuCollapse ? (
										<FiArrowRightCircle />
									) : (
										<FiArrowLeftCircle />
									)}
								</div>
							</SidebarHeader>
							<SidebarContent ref={sidebarContentRef}>
								<Menu>
									{renderMenuItems(sidebarMenus)}
								</Menu>
							</SidebarContent>

							<SidebarFooter className="sidebar-footer-panel" style={{ borderTop: "1px solid #ddd" }}>
								<div
									className="sidebar-footer-user d-flex align-items-center justify-content-center cursor-pointer"
									style={{ height: "34px" }}
									onClick={() => {
										if (menuCollapse) {
											menuIconClick();
										} else if (!isMobile || hasDropdownContent) {
											setShowDropdown(!showDropdown);
										}
									}}>
									<div style={{
										width: 32, height: 32,
										borderRadius: "50%",
										background: "rgba(255,255,255,0.15)",
										border: "1.5px solid var(--primary-color)",
										display: "flex", alignItems: "center", justifyContent: "center",
										marginRight: menuCollapse ? 0 : 10,
										marginLeft: menuCollapse ? 0 : "5px",
										flexShrink: 0
									}}>
										<FiUser style={{ fontSize: "17px", color: "var(--primary-color)" }} />
									</div>
								{!menuCollapse && (
										<>
											<span className="user-name">
												{`${userData?.firstName || ""} ${userData?.lastName || ""}`}
											</span>
										{(!isMobile || hasDropdownContent) && (
											<FontAwesomeIcon
												icon={faCaretUp}
												className='user-dd-icon'
												style={{
													transform: showDropdown ? "rotate(-180deg)" : "rotate(0deg)",
												}}
											/>
										)}
										</>
									)}
								</div>

							{!menuCollapse && showDropdown && (
									<div
										className="sidebar-footer-dropdown"
										style={{
											borderTop: "solid 1px #c7c7c7",
										}}
									>
								{/* Reset Password Option - Show only when user has a password set */}
								{userData?.hasPassword && (
											<div className="reset-password-option" style={{ borderBottom: "solid 1px #e0e0e0" }}>
												<div
													className="d-flex align-items-center cursor-pointer reset-password-row"
													onClick={handleResetPassword}
												>
													<FontAwesomeIcon icon={faKey} className="me-2" style={{ fontSize: "14px", color: "#666" }} />
													<span style={{ fontSize: "13px", color: "#333", fontWeight: "400" }}>Reset Password</span>
												</div>
											</div>
										)}


										{/* Authorized User Selector */}
										{userData?.primaryUsers?.primaryUsers && userData.primaryUsers.primaryUsers.length > 0 && (
											<div className="authorized-user-selector" style={{ borderBottom: "solid 1px #e0e0e0" }}>
												<div className="d-flex align-items-center mb-1">
													<FontAwesomeIcon icon={faUserTag} className="me-2" style={{ fontSize: "14px", color: "#666" }} />
													<span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>Authorized User For</span>
												</div>
												<select
													className="form-select form-select-sm"
													value={selectedAuthorizedUser?.userId || userData.userId || ''}
													onChange={e => {
														const selectedId = Number(e.target.value);
														const allUsers = [userData, ...userData.primaryUsers.primaryUsers.filter(u => u.userId !== userData.userId)];
														const selectedUser = allUsers.find(u => u.userId === selectedId);
														dispatch({ type: SET_AUTHORIZED_USER, payload: selectedUser });
														navigate(BASE_PATH_PREFIX, { replace: true });
													}}
													style={{ fontSize: "13px", marginLeft: "22px", width: "calc(100% - 22px)" }}
												>
													{/* Show all primary users from the response */}
													{/* Logged in user first, styled differently */}
													<option
														key={userData.userId}
														value={userData.userId}
														data-userid={userData.userId}
														style={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#388e3c' }}
													>
														{userData.firstName} {userData.lastName} (Logged in user)
													</option>

													{/* Other primary users except logged in user */}
													{userData.primaryUsers.primaryUsers
														.filter(primaryUser => primaryUser.userId !== userData.userId)
														.map(primaryUser => (
															<option
																key={primaryUser.userId}
																value={primaryUser.userId}
																data-userid={primaryUser.userId}
															>
																{primaryUser.firstName} {primaryUser.lastName}
															</option>
														))}
												</select>
											</div>
										)}

										{/* Role Selector */}
										{userData?.rolesList?.items && userData.rolesList.items.length > 1 && (
											<div className="role-selector">
												<div className="d-flex align-items-center role-selector-label">
													<FontAwesomeIcon icon={faUserTag} className="me-2" style={{ fontSize: "14px", color: "#666" }} />
													<span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>Role</span>
												</div>
												<select
													className="form-select form-select-sm role-selector-select"
													value={selectedRole?.code || ''}
													onChange={(e) => {
														const role = userData.rolesList.items.find(r => r.code === e.target.value);
														if (role && role.code !== selectedRole?.code) {
															setIsRoleChanging(true);
															setCookie('selectedRole', role);
															dispatch({ type: UPDATE_COMMON_REDUCER, payload: { selectedRole: role } });
															// Use replace to avoid adding to history stack
															navigate(BASE_PATH_PREFIX, { replace: true });
															// Hide loader after navigation
															setTimeout(() => setIsRoleChanging(false), 500);
														}
													}}
													style={{ fontSize: "13px" }}
												>
													{userData.rolesList.items.map(role => (
														<option key={role.id} value={role.code}>
															{role.name}
														</option>
													))}
												</select>
											</div>
										)}

									</div>
								)}

							{/* Always-visible sign-out, outside the profile dropdown — on every
								device, not just mobile, so it's reachable without opening the
								dropdown first. */}
						{!menuCollapse && (
							<div
								className="d-flex align-items-center sign-out-option"
								style={{ borderTop: "solid 1px #c7c7c7" }}
							>
								<FiLogOut className="sign-out-icon cursor-pointer" onClick={handleLogout} />
								<span className="cursor-pointer" onClick={handleLogout}>Sign out</span>
							</div>
						)}
						</SidebarFooter>
						</ProSidebar>
					</div>
				</div>
			</div>

			{/* Tooltip div for collapsed sidebar menu items */}
			{menuCollapse && hoveredMenuLabel && (
				<div 
					className="sidebar-tooltip"
					style={{
						top: `${tooltipPosition.top}px`,
						left: `${tooltipPosition.left}px`
					}}
				>
					{hoveredMenuLabel}
				</div>
			)}

			{/* Reset Password Modal (global, controlled by Redux) */}
			<ResetPassword
				show={!!showResetPasswordModal}
				onClose={handleCloseResetPasswordModal}
				onSubmit={handleSubmitResetPassword}
			/>
		</Loader>
	)
}

export default Sidebar;
